import { StepProps, Steps } from "antd"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import ApprovedAttachmentUrl from "./ApprovedAttachmentUrl"
import { collapseProposalEvents, Proposal, ProposalEvent } from "./proposalsModel"
import { useProposalEventsQuery } from "./proposalsQueries"

dayjs.extend(utc)

type Props = {
    latestProposal: Proposal
}

type EventStep = StepProps & {
    timestamp: string
}

const ProposalTimeline = ({ latestProposal }: Props) => {
    const proposalEventsQuery = useProposalEventsQuery(latestProposal.id)
    const proposalEvents = proposalEventsQuery.data ?? []

    const eventTitles: Record<string, string> = {
        'proposal-sent': 'Sent',
        'proposal-pdf-viewed': 'PDF Viewed',
        'proposal-customer-portal-viewed': 'Proposal Viewed',
        'proposal-previewed': 'Proposal Previewed',
        'proposal-pdf-previewed': 'Proposal PDF Previewed',
        'follow-up-sent': 'Follow-Up Sent',
        'follow-up-disabled': 'Follow-Up Disabled by Customer',
        'follow-up-tenant-disabled': 'Follow-Up Disabled'
    }

    const populateEventTimeline = (events: ProposalEvent[]) => {
        const timeline: EventStep[] = [];

        timeline.push({
            title: 'Created',
            description: latestProposal.initiateDateUtc ? `${dayjs.utc(latestProposal.initiateDateUtc).local().format("MMM D YYYY h:mma")} by ${latestProposal.initiatedBy}` : '',
            timestamp: latestProposal.initiateDateUtc
        })

        if (latestProposal.isApproved) {
            timeline.push({
                title: <ApprovedAttachmentUrl latestProposal={latestProposal} />,
                description: latestProposal.approveDateUtc ? `${dayjs.utc(latestProposal.approveDateUtc).local().format("MMM D YYYY h:mma")}` : '',
                timestamp: latestProposal.approveDateUtc
            })
        }

        const formatTimestamp = (timestamp: string) => dayjs.utc(timestamp).local().format("MMM D YYYY h:mma")

        collapseProposalEvents(events).forEach(event => {
            const baseTitle = eventTitles[event.eventType] ?? 'Unknown event'
            timeline.push({
                title: event.count > 1 ? `${baseTitle} (${event.count})` : baseTitle,
                description: <div>
                    {!event.email?.includes('anonymous-customer@synkedup') &&
                        <div>
                            {event.email?.toLowerCase()}
                        </div>
                    }
                    <div>
                        {event.count > 1
                            ? `${formatTimestamp(event.earliestTimestamp)} - ${formatTimestamp(event.latestTimestamp)}`
                            : formatTimestamp(event.earliestTimestamp)}
                    </div>
                </div>,
                timestamp: event.earliestTimestamp
            })
        })

        timeline.sort((x, y) => x.timestamp.localeCompare(y.timestamp))

        return timeline
    }

    const timeline = populateEventTimeline(proposalEvents)

    return <Steps
        progressDot
        direction="vertical"
        current={timeline.length}
        items={timeline}
    />
}

export default ProposalTimeline
